/* Public Loox Storefront API only. Never put a Merchant API secret in this asset. */
if (!customElements.get('pw-social-proof')) {
  customElements.define('pw-social-proof', class extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-loox')) return;
      this.controller = new AbortController();
      this.reviews = this.querySelector('[data-loox-reviews]');
      this.videos = this.querySelector('[data-loox-videos]');
      this.reviews.classList.add('pw-sp__review-track');
      this.reviewCarousel = this.querySelector('[data-loox-review-carousel]') || this.reviews;
      this.reviews.replaceChildren();
      this.videos.replaceChildren();
      this.previewPane = this.previewNative = null;
      this.photos = [];
      this.videoReviews = [];
      this.seenReviews = new Set();
      this.visitedPages = new Set();
      this.videoIndex = 0;
      this.videoPosition = 0;
      this.videoAnimating = this.videoAdvancing = false;
      this.feedError = false;
      this.feedPromise = null;
      this.previewHovered = this.previewFocused = false;
      this.stageVisible = false;
      this.previewToken = 0;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.dialog = this.querySelector('[data-loox-dialog]');
      this.player = this.querySelector('[data-loox-player]');
      this.loading = this.querySelector('[data-loox-loading]');
      this.status = this.querySelector('[data-loox-status]');
      // Also remove the old footer when this asset is used with an earlier section template.
      this.querySelectorAll('[data-loox-load-more], [data-loox-preview-toggle], .pw-sp__feed-footer, .pw-sp__press-row').forEach(node => node.remove());
      this.addEventListener('click', this.onClick);
      this.dialog.addEventListener('close', this.onDialogClose);
      this.player.addEventListener('error', this.onVideoError);
      this.reviews.addEventListener('scroll', this.onReviewScroll, { passive: true });
      this.videos.addEventListener('pointerdown', this.onPointerDown);
      this.videos.addEventListener('pointerup', this.onPointerUp);
      this.videos.addEventListener('pointercancel', this.onPointerCancel);
      this.videos.addEventListener('pointerenter', this.onPreviewEnter);
      this.videos.addEventListener('pointerleave', this.onPreviewLeave);
      this.videos.addEventListener('focusin', this.onPreviewFocus);
      this.videos.addEventListener('focusout', this.onPreviewBlur);
      this.videos.addEventListener('keydown', this.onVideoKey);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.fonts?.addEventListener('loadingdone', this.onReviewFonts);
      this.reducedMotion.addEventListener('change', this.onVisibilityChange);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => { this.layoutVideos(); this.layoutReviews(); this.updateArrows(); });
        this.resizeObserver.observe(this.videos);
        this.resizeObserver.observe(this.reviews);
      }
      if ('IntersectionObserver' in window) {
        this.stageObserver = new IntersectionObserver(entries => {
          this.stageVisible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.25);
          this.refreshPreview();
        }, { threshold: 0.25 });
        this.stageObserver.observe(this.videos);
      } else this.stageVisible = true;

      const storeId = (this.dataset.storeId || '').trim();
      if (!storeId) {
        this.showEditorMessage('Enter your Loox Public Store ID in this section’s settings to load photo reviews and videos.');
        return;
      }
      // Catch accidentally pasted secret keys and URLs before making any request.
      if (!/^[A-Za-z0-9_.-]+$/.test(storeId) || /^lx_ak_/i.test(storeId)) {
        this.showEditorMessage('Use the publicStoreId from Loox Settings → API Keys, rather than a secret key or URL.');
        return;
      }
      this.apiBase = `https://storefront-api.loox.io/storefront/v1/store/${encodeURIComponent(storeId)}`;
      const firstPage = new URL(`${this.apiBase}/product-reviews`);
      firstPage.searchParams.set('limit', '50');
      firstPage.searchParams.set('sort', this.dataset.sort === 'date' ? 'date' : 'featured');
      if (this.dataset.locale) firstPage.searchParams.set('locale', this.dataset.locale);
      this.firstPage = firstPage;
      this.nextPage = firstPage.href;
      if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
        this.load();
      } else {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.observer.disconnect();
            this.load();
          }
        }, { rootMargin: '400px' });
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.stageObserver?.disconnect();
      this.stopPreview();
      clearTimeout(this.discoveryTimer);
      clearTimeout(this.videoAnimationTimer);
      this.removeEventListener('click', this.onClick);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      this.player?.removeEventListener('error', this.onVideoError);
      this.reviews?.removeEventListener('scroll', this.onReviewScroll);
      this.videos?.removeEventListener('pointerdown', this.onPointerDown);
      this.videos?.removeEventListener('pointerup', this.onPointerUp);
      this.videos?.removeEventListener('pointercancel', this.onPointerCancel);
      this.videos?.removeEventListener('pointerenter', this.onPreviewEnter);
      this.videos?.removeEventListener('pointerleave', this.onPreviewLeave);
      this.videos?.removeEventListener('focusin', this.onPreviewFocus);
      this.videos?.removeEventListener('focusout', this.onPreviewBlur);
      this.videos?.removeEventListener('keydown', this.onVideoKey);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.fonts?.removeEventListener('loadingdone', this.onReviewFonts);
      this.reducedMotion?.removeEventListener('change', this.onVisibilityChange);
      if (this.dialog?.open) this.dialog.close();
      this.stopVideo();
    }

    showEditorMessage(message) {
      if (!window.Shopify?.designMode) return;
      this.status.textContent = message;
      this.status.hidden = false;
    }

    async load() {
      const controller = this.controller;
      this.setAttribute('aria-busy', 'true');
      this.loading.hidden = false;
      this.status.hidden = true;
      try {
        const results = await Promise.allSettled([
          this.fetchJSON(`${this.apiBase}/product-reviews/bottomline`, controller.signal),
          this.loadMore({ photos: 4, videos: 5, user: true })
        ]);
        if (!this.isConnected || controller !== this.controller) return;
        if (results[0].status === 'fulfilled') this.renderStats(results[0].value);
        if (!this.photos.length && !this.videoReviews.length && !this.feedError) {
          this.showEditorMessage(this.nextPage ? 'Looking for published customer media in subsequent Loox pages…' : 'No published photo reviews with text or video reviews were found in Loox.');
        }
        if (results[1].status === 'rejected' || this.feedError) {
          this.showEditorMessage('Loox reviews could not be loaded. Check the publicStoreId, API access on your plan, and allowed storefront/preview domains.');
        }
      } finally {
        if (this.isConnected && controller === this.controller) {
          this.loading.hidden = true;
          this.removeAttribute('aria-busy');
        }
      }
    }

    async fetchJSON(url, signal) {
      const requestController = new AbortController();
      const abort = () => requestController.abort();
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) abort();
      const timeout = setTimeout(abort, 15000);
      try {
        const response = await fetch(url, { signal: requestController.signal, credentials: 'omit', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Loox HTTP ${response.status}`);
        return await response.json();
      } finally { clearTimeout(timeout); signal.removeEventListener('abort', abort); }
    }

    async loadMore({ photos = this.photos.length + 4, videos = this.videoReviews.length + 5, user = false } = {}) {
      if (this.feedPromise) return this.feedPromise;
      if (!this.nextPage || (this.feedError && !user)) return;
      const controller = this.controller;
      this.feedPromise = (async () => {
        this.feedError = false;
        try {
          // Three pages per interaction, retaining the cursor: there is no total page/review cap.
          for (let batch = 0; batch < 3 && this.nextPage; batch++) {
            const url = this.nextPage;
            if (this.visitedPages.has(url)) { this.nextPage = null; break; }
            const payload = await this.fetchJSON(url, controller.signal);
            if (!Array.isArray(payload.reviews)) throw new Error('Invalid Loox response');
            if (!this.isConnected || controller !== this.controller) return;
            this.visitedPages.add(url);
            for (const review of payload.reviews) {
              if (!review?.id || this.seenReviews.has(review.id)) continue;
              this.seenReviews.add(review.id);
              const media = Array.isArray(review.media) ? review.media : [];
              const video = media.find(item => item?.type === 'video' && this.safeURL(item.url));
              const photo = media.find(item => ['photo', 'image'].includes(item?.type) && this.safeURL(item.url));
              if (video) this.videoReviews.push({ ...review, selectedMedia: video });
              else if (photo && typeof review.body === 'string' && review.body.trim()) {
                const data = { ...review, selectedMedia: photo };
                this.photos.push(data);
                this.reviews.append(this.photoCard(data));
              }
            }
            this.nextPage = null;
            if (payload.pagination?.hasMore && payload.pagination.nextPageUrl) {
              const next = new URL(payload.pagination.nextPageUrl, this.firstPage.origin);
              if (next.origin !== this.firstPage.origin || next.pathname !== this.firstPage.pathname || this.visitedPages.has(next.href)) throw new Error('Invalid pagination URL');
              this.nextPage = next.href;
            }
            this.renderFeeds();
            if (this.photos.length >= photos && this.videoReviews.length >= videos) break;
          }
        } catch (_) {
          if (this.isConnected && controller === this.controller) {
            this.feedError = true;
            this.showEditorMessage('A Loox page could not be loaded. Existing reviews remain available; browse the sliders to retry.');
          }
        } finally {
          if (controller === this.controller) {
            this.feedPromise = null;
            const status = this.querySelector('[data-loox-feed-status]');
            if (status) {
              status.textContent = this.feedError ? 'More reviews could not be loaded. Use the slider arrows to try again.' : '';
              status.hidden = !this.feedError;
            }
            this.updateArrows();
            // If early pages have no usable media, continue searching automatically.
            // Once a slider is available, browsing it requests subsequent pages.
            clearTimeout(this.discoveryTimer);
            if (this.isConnected && this.nextPage && !this.feedError && !this.photos.length && !this.videoReviews.length) {
              this.discoveryTimer = setTimeout(() => {
                if (this.isConnected && controller === this.controller) this.loadMore({ photos: 4, videos: 5 });
              }, 300);
            }
          }
        }
      })();
      return this.feedPromise;
    }

    renderFeeds() {
      this.reviews.hidden = false;
      this.reviewCarousel.hidden = this.photos.length === 0;
      this.querySelector('[data-loox-ugc-header]').hidden = this.videoReviews.length === 0;
      this.querySelector('[data-loox-carousel]').hidden = this.videoReviews.length === 0;
      if (!this.videoAnimating) this.renderVideoWindow();
      this.layoutReviews();
      this.updateArrows();
    }

    safeURL(value) {
      if (typeof value !== 'string' || !value.trim()) return '';
      try {
        const url = new URL(value, window.location.origin);
        return url.protocol === 'https:' && !url.username && !url.password ? url.href : '';
      } catch (_) {
        return '';
      }
    }

    productURL(review) {
      const url = this.safeURL(review.product?.url);
      if (!url) return '';
      const parsed = new URL(url);
      // Shopify's product URL can use myshopify.com; stay on the current storefront/market.
      const productPath = parsed.pathname.match(/\/products\/[^/]+\/?$/);
      return productPath ? `${window.Shopify?.routes?.root || '/'}${productPath[0].slice(1)}` : url;
    }

    cloudflarePlayerURL(value) {
      const safe = this.safeURL(value);
      if (!safe) return '';
      const url = new URL(safe);
      const isPlayer = url.hostname === 'iframe.videodelivery.net' ||
        (url.hostname.endsWith('.cloudflarestream.com') && /\/iframe\/?$/.test(url.pathname));
      if (!isPlayer) return '';
      url.searchParams.set('autoplay', 'true');
      url.searchParams.set('controls', 'true');
      return url.href;
    }

    element(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }

    reviewerName(review) {
      return typeof review.reviewer?.name === 'string' && review.reviewer.name.trim() ? review.reviewer.name : 'Customer';
    }

    stars(rating, className = 'pw-sp__review-stars') {
      const count = Math.min(5, Math.max(0, Math.round(Number(rating) || 0)));
      const node = this.element('div', className, '★'.repeat(count) + '☆'.repeat(5 - count));
      node.setAttribute('role', 'img');
      node.setAttribute('aria-label', `${count} out of 5 stars`);
      return node;
    }

    renderStats(stats) {
      const total = Number(stats?.totalReviews);
      const average = Number(stats?.averageRating);
      if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(average) || average < 0 || average > 5) return;
      this.querySelector('[data-loox-average]').textContent = average.toFixed(1);
      this.querySelector('[data-loox-count]').textContent = total >= 100 ? `${Math.floor(total / 100) * 100}+` : String(Math.floor(total));
      const rating = this.querySelector('[data-loox-rating]');
      rating.setAttribute('aria-label', `${average.toFixed(1)} out of 5 stars`);
      rating.style.setProperty('--pw-rating-fill', `${average / 5 * 100}%`);
      this.querySelector('[data-loox-stats]').hidden = false;
    }

    photoCard(review) {
      const card = this.element('article', 'pw-sp__review-card pw-sp__review-card--photo');
      card.dataset.reviewId = review.id;
      const name = this.reviewerName(review);
      const photo = this.element('img', 'pw-sp__review-photo');
      photo.src = this.safeURL(review.selectedMedia.url);
      photo.alt = `Customer photo from ${name}${review.product?.name ? ` reviewing ${review.product.name}` : ''}`;
      photo.loading = 'lazy';
      photo.decoding = 'async';
      photo.width = 600;
      photo.height = 450;
      photo.addEventListener('error', () => { photo.style.visibility = 'hidden'; });
      const row = this.element('div', 'pw-sp__reviewer-row');
      const avatar = this.element('div', 'pw-sp__avatar');
      avatar.setAttribute('aria-hidden', 'true');
      avatar.append(this.element('span', 'pw-sp__avatar-placeholder', name.trim().slice(0, 1).toUpperCase()));
      const info = this.element('div', 'pw-sp__reviewer-info');
      info.append(this.element('span', 'pw-sp__reviewer-name', name));
      if (review.verified === true) info.append(this.element('span', 'pw-sp__verified-badge', '✓ VERIFIED BUYER'));
      row.append(avatar, info);
      card.append(photo, row);
      const date = new Date(review.date);
      if (!Number.isNaN(date.getTime())) {
        const time = this.element('time', 'pw-sp__review-date', date.toLocaleDateString(this.dataset.locale || undefined, { timeZone: 'UTC' }));
        time.dateTime = date.toISOString();
        card.append(time);
      }
      card.append(this.stars(review.rating));
      const quote = this.element('p', 'pw-sp__quote pw-sp__quote--collapsed', review.body);
      quote.id = `${this.id}-review-${review.id}`;
      card.append(quote);
      const toggle = this.element('button', 'pw-sp__read-more', 'See more');
      toggle.type = 'button';
      toggle.hidden = true;
      toggle.setAttribute('aria-controls', quote.id);
      toggle.setAttribute('aria-expanded', 'false');
      toggle.addEventListener('click', () => {
        const collapsed = quote.classList.toggle('pw-sp__quote--collapsed');
        card.classList.toggle('is-expanded', !collapsed);
        toggle.setAttribute('aria-expanded', String(!collapsed));
        toggle.textContent = collapsed ? 'See more' : 'See less';
      });
      card.append(toggle);
      if (review.product?.name) {
        const productURL = this.productURL(review);
        const product = this.element(productURL ? 'a' : 'span', 'pw-sp__product-label', `REVIEWING: ${review.product.name}`);
        if (productURL) product.href = productURL;
        card.append(product);
      }
      return card;
    }

    layoutReviews() {
      if (!this.reviews.clientWidth) return;
      for (const card of this.reviews.children) {
        const quote = card.querySelector('.pw-sp__quote');
        const toggle = card.querySelector('.pw-sp__read-more');
        if (!quote || !toggle) continue;
        // Measure wrapping at the current card width, including short reviews with line breaks.
        toggle.hidden = !card.classList.contains('is-expanded') && quote.scrollHeight <= quote.clientHeight + 1;
      }
    }

    onReviewFonts = () => this.layoutReviews();

    videoCard(review) {
      const card = this.element('article', 'pw-sp__ugc-card pw-sp__ugc-card--video');
      card.dataset.reviewId = review.id;
      const name = this.reviewerName(review);
      const button = this.element('button', 'pw-sp__video-play');
      button.type = 'button';
      button.setAttribute('aria-label', `Play customer video by ${name}`);
      button.review = review;
      button.dataset.looxPlay = '';
      const image = this.element('div', 'pw-sp__ugc-image');
      const poster = this.safeURL(review.selectedMedia.thumbnailUrl);
      if (poster) {
        const img = this.element('img');
        img.src = poster;
        img.alt = '';
        img.loading = 'eager';
        img.decoding = 'async';
        img.width = 600;
        img.height = 1067;
        img.addEventListener('error', () => { img.hidden = true; });
        image.append(img);
      }
      const icon = this.element('span', 'pw-sp__play-icon', '▶');
      icon.setAttribute('aria-hidden', 'true');
      button.append(image, icon);
      const reviewer = this.element('span', 'pw-sp__video-reviewer', `${name}${review.verified === true ? ' ✓' : ''}`);
      if (review.verified === true) reviewer.setAttribute('aria-label', `${name}, verified buyer`);
      const footer = this.element('div', 'pw-sp__ugc-footer pw-sp__video-footer');
      const details = this.element('div', 'pw-sp__video-details');
      details.append(reviewer, this.stars(review.rating));
      footer.append(details);
      card.append(button, footer);
      return card;
    }

    renderVideoWindow(direction = 0) {
      const total = this.videoReviews.length;
      if (!total) return;
      const normalize = position => (position % total + total) % total;
      if (normalize(this.videoPosition) !== this.videoIndex) this.videoPosition = this.videoIndex;
      // Two off-screen cards give both edges room to enter and exit before recycling.
      const count = total >= 5 ? 7 : total;
      const animate = !!direction && !this.reducedMotion.matches;
      const existing = new Map(Array.from(this.videos.querySelectorAll('article')).map(card => [Number(card.dataset.position), card]));
      const keep = new Set();
      const center = Math.floor(count / 2);
      const entering = [];
      this.layoutVideos();
      const step = parseFloat(this.videos.style.getPropertyValue('--pw-video-width')) + 16;
      for (let slot = 0; slot < count; slot++) {
        const offset = slot - center;
        const position = this.videoPosition + offset;
        const index = normalize(position);
        const review = this.videoReviews[index];
        let card = existing.get(position);
        if (card && card.dataset.reviewId !== String(review.id)) { card.remove(); card = null; }
        if (!card) {
          card = this.videoCard(review);
          card.dataset.position = position;
          if (animate) {
            const start = offset + direction;
            card.classList.add('is-entering');
            card.style.setProperty('--pw-video-offset', `${start * step}px`);
            card.style.setProperty('--pw-video-scale', Math.abs(start) === 0 ? '1' : Math.abs(start) === 1 ? '0.92' : '0.84');
            entering.push(card);
          }
        }
        keep.add(position);
        card.dataset.slot = offset;
        card.dataset.videoIndex = index;
        card.classList.toggle('is-active', offset === 0);
        card.classList.toggle('is-near', Math.abs(offset) === 1);
        if (!card.isConnected) this.videos.append(card);
      }
      for (const [position, card] of existing) if (!keep.has(position)) {
        if (!animate) card.remove();
        else {
          card.dataset.slot = position - this.videoPosition;
          card.classList.remove('is-active', 'is-near');
        }
      }
      if (animate) {
        // Commit entering cards outside the viewport before changing their transforms.
        void this.videos.offsetWidth;
        entering.forEach(card => card.classList.remove('is-entering'));
        this.videoAnimating = true;
        clearTimeout(this.videoAnimationTimer);
        this.videoAnimationTimer = setTimeout(() => {
          if (!this.isConnected) return;
          this.videoAnimating = false;
          this.renderVideoWindow();
          this.updateArrows();
        }, 400);
      }
      this.layoutVideos();
      this.refreshPreview();
    }

    layoutVideos() {
      const width = this.videos.clientWidth;
      if (!width) return;
      const visible = window.innerWidth >= 1100 ? 5 : window.innerWidth >= 750 ? 3 : 1;
      const cardWidth = visible === 1 ? Math.min(320, width * 0.82) : (width - (visible - 1) * 16) / visible;
      this.videos.style.setProperty('--pw-video-width', `${cardWidth}px`);
      this.videos.style.setProperty('--pw-video-height', `${cardWidth * 16 / 9}px`);
      for (const card of this.videos.querySelectorAll('article')) {
        const slot = Number(card.dataset.slot);
        card.style.setProperty('--pw-video-offset', `${slot * (cardWidth + 16)}px`);
        card.style.setProperty('--pw-video-scale', slot === 0 ? '1' : Math.abs(slot) === 1 ? '0.92' : '0.84');
        card.inert = Math.abs(slot) > Math.floor(visible / 2);
      }
    }

    async advanceVideo(direction, user = false) {
      if (!this.videoReviews.length || this.videoAnimating || this.videoAdvancing) return;
      this.videoAdvancing = true;
      this.updateArrows();
      try {
        this.stopPreview();
        if (direction > 0 && this.nextPage && this.videoIndex >= this.videoReviews.length - 3) {
          await this.loadMore({ photos: 0, videos: this.videoReviews.length + 5, user });
        }
        if (!this.isConnected) return;
        const total = this.videoReviews.length;
        if ((this.videoPosition % total + total) % total !== this.videoIndex) this.videoPosition = this.videoIndex;
        if (total > 1) this.videoPosition += direction;
        this.videoIndex = (this.videoPosition % total + total) % total;
        this.renderVideoWindow(total > 1 ? direction : 0);
      } finally {
        this.videoAdvancing = false;
        this.updateArrows();
        if (!this.videoAnimating) this.refreshPreview();
      }
    }

    async advanceReviews(direction) {
      const rtl = getComputedStyle(this.reviews).direction === 'rtl' ? -1 : 1;
      const max = this.reviews.scrollWidth - this.reviews.clientWidth;
      if (direction > 0 && this.nextPage && Math.abs(this.reviews.scrollLeft) >= max - this.reviews.clientWidth) {
        await this.loadMore({ photos: this.photos.length + 4, videos: 0, user: true });
      }
      this.reviews.scrollBy({ left: direction * rtl * (this.reviews.clientWidth + 16), behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
    }

    onReviewScroll = () => {
      this.updateArrows();
      if (this.nextPage && !this.feedPromise && !this.feedError && Math.abs(this.reviews.scrollLeft) >= this.reviews.scrollWidth - this.reviews.clientWidth * 2) {
        this.loadMore({ photos: this.photos.length + 4, videos: 0 });
      }
    };

    onPointerDown = event => { if (event.button === 0) this.swipeStart = { x: event.clientX, y: event.clientY }; };
    onPointerCancel = () => { this.swipeStart = null; };
    onPointerUp = event => {
      if (!this.swipeStart) return;
      const dx = event.clientX - this.swipeStart.x;
      const dy = event.clientY - this.swipeStart.y;
      this.swipeStart = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        this.suppressClickUntil = Date.now() + 400;
        this.advanceVideo(dx < 0 ? 1 : -1, true);
      }
    };
    onVideoKey = event => {
      if (event.target !== this.videos || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.advanceVideo(event.key === 'ArrowRight' ? 1 : -1, true);
    };
    onPreviewEnter = event => { if (event.pointerType !== 'touch') { this.previewHovered = true; this.stopPreview(); } };
    onPreviewLeave = () => { this.previewHovered = false; this.refreshPreview(); };
    onPreviewFocus = () => { this.previewFocused = true; this.stopPreview(); };
    onPreviewBlur = () => { queueMicrotask(() => { this.previewFocused = this.videos.contains(document.activeElement); this.refreshPreview(); }); };
    onVisibilityChange = () => this.refreshPreview();

    previewAllowed() {
      return this.isConnected && this.stageVisible && !document.hidden && !this.dialog.open &&
        !this.videoAnimating && !this.videoAdvancing && !this.previewHovered && !this.previewFocused && !this.reducedMotion.matches;
    }

    async streamSDK() {
      if (typeof window.Stream === 'function') return window.Stream;
      const type = this.constructor;
      if (!type.streamPromise) {
        type.streamPromise = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://embed.cloudflarestream.com/embed/sdk.latest.js';
          script.async = true;
          const timer = setTimeout(() => { script.remove(); reject(new Error('Stream SDK timeout')); }, 12000);
          script.onload = () => { clearTimeout(timer); typeof window.Stream === 'function' ? resolve(window.Stream) : reject(new Error('Stream SDK unavailable')); };
          script.onerror = () => { clearTimeout(timer); script.remove(); reject(new Error('Stream SDK unavailable')); };
          document.head.append(script);
        }).catch(error => { type.streamPromise = null; throw error; });
      }
      return type.streamPromise;
    }

    async refreshPreview() {
      const review = this.videoReviews[this.videoIndex];
      if (!review || !this.previewAllowed()) { this.stopPreview(); return; }
      if (this.previewReviewId === review.id) return;
      this.stopPreview();
      const token = this.previewToken;
      this.previewReviewId = review.id;
      const embedURL = this.cloudflarePlayerURL(review.selectedMedia.url);
      try {
        const Stream = embedURL ? await this.streamSDK() : null;
        if (token !== this.previewToken || !this.previewAllowed()) return;
        if (!this.previewPane) {
          this.previewPane = this.element('div', 'pw-sp__preview-pane');
          this.previewPane.setAttribute('aria-hidden', 'true');
        }
        this.previewCard = this.videos.querySelector('article.is-active');
        if (!this.previewCard) return;
        this.previewCard.append(this.previewPane);
        this.previewPane.hidden = false;
        this.previewStarted = false;
        if (embedURL) {
          this.previewFrame = this.element('iframe', 'pw-sp__inline-preview');
          this.previewFrame.title = 'Muted customer video preview';
          this.previewFrame.tabIndex = -1;
          this.previewFrame.allow = 'autoplay; encrypted-media';
          const url = new URL(embedURL);
          url.searchParams.set('muted', 'true');
          url.searchParams.set('controls', 'false');
          this.previewFrame.src = url.href;
          this.previewPane.append(this.previewFrame);
          this.previewSDK = Stream(this.previewFrame);
          this.previewSDK.addEventListener('playing', this.onPreviewPlaying);
          this.previewSDK.addEventListener('timeupdate', this.onPreviewTime);
          this.previewSDK.addEventListener('ended', this.onPreviewEnded);
          this.previewMedia = this.previewSDK;
        } else {
          if (!this.previewNative) {
            this.previewNative = this.element('video', 'pw-sp__inline-preview');
            this.previewNative.muted = true;
            this.previewNative.playsInline = true;
            this.previewNative.tabIndex = -1;
            this.previewNative.addEventListener('playing', this.onPreviewPlaying);
            this.previewNative.addEventListener('timeupdate', this.onPreviewTime);
            this.previewNative.addEventListener('ended', this.onPreviewEnded);
            this.previewPane.append(this.previewNative);
          }
          this.previewNative.src = this.safeURL(review.selectedMedia.url);
          this.previewNative.hidden = false;
          if (this.previewFrame) this.previewFrame.hidden = true;
          this.previewMedia = this.previewNative;
          this.previewNative.play().catch(() => {});
        }
        if (embedURL) {
          this.previewFrame.hidden = false;
          if (this.previewNative) this.previewNative.hidden = true;
        }
        // Skip an unavailable preview without stopping the carousel.
        this.previewStallTimer = setTimeout(() => { if (token === this.previewToken && this.previewAllowed()) this.advanceVideo(1); }, 15000);
      } catch (_) {
        if (token === this.previewToken) this.previewStallTimer = setTimeout(() => { if (this.previewAllowed()) this.advanceVideo(1); }, 5000);
      }
    }

    onPreviewPlaying = () => {
      if (!this.previewAllowed() || !this.previewReviewId) return;
      this.previewStarted = true;
      this.onPreviewTime();
    };
    onPreviewTime = () => {
      if (!this.previewStarted || !this.previewAllowed()) return;
      const time = Number(this.previewMedia?.currentTime);
      // Keep the poster visible until the player has actually rendered its first frames.
      if (time > 0) this.previewCard?.classList.add('preview-ready');
      if (time >= 5) this.advanceVideo(1);
    };
    onPreviewEnded = () => { if (this.previewStarted && this.previewAllowed()) this.advanceVideo(1); };

    stopPreview() {
      this.previewToken++;
      this.previewReviewId = null;
      this.previewStarted = false;
      clearTimeout(this.previewStallTimer);
      // Release the SDK's global listeners and cached playback state before the next video.
      try { this.previewSDK?.destroy(); } catch (_) {}
      this.previewSDK = null;
      this.previewFrame?.remove();
      this.previewFrame = null;
      this.previewMedia = null;
      if (this.previewNative) {
        this.previewNative.pause();
        this.previewNative.removeAttribute('src');
        this.previewNative.load();
      }
      if (this.previewPane) this.previewPane.hidden = true;
      this.videos?.querySelectorAll('.preview-ready').forEach(card => card.classList.remove('preview-ready'));
    }

    onClick = (event) => {
      if (Date.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); return; }
      const play = event.target.closest('[data-loox-play]');
      if (play && this.contains(play)) {
        const review = play.review;
        const url = this.safeURL(review.selectedMedia.url);
        if (!url) return;
        this.stopPreview();
        this.stopVideo();
        this.lastPlayButton = play;
        this.querySelector('[data-loox-video-title]').textContent = `Customer video by ${this.reviewerName(review)}`;
        const link = this.querySelector('[data-loox-video-product]');
        const productURL = this.productURL(review);
        link.hidden = !productURL;
        if (productURL) link.href = productURL;
        else link.removeAttribute('href');
        this.querySelector('[data-loox-video-error]').hidden = true;
        const embedURL = this.cloudflarePlayerURL(url);
        if (embedURL) {
          // Loox can return a Cloudflare HTML player page, rather than a media file.
          this.player.hidden = true;
          this.embed = this.element('iframe', 'pw-sp__video-embed');
          this.embed.title = `Customer video by ${this.reviewerName(review)}`;
          this.embed.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
          this.embed.allowFullscreen = true;
          this.embed.src = embedURL;
          this.player.before(this.embed);
        } else {
          this.player.hidden = false;
          this.player.poster = this.safeURL(review.selectedMedia.thumbnailUrl);
          this.player.src = url;
        }
        this.dialog.showModal();
        if (!embedURL) this.player.play().catch(() => { /* Native controls allow playback if autoplay is blocked. */ });
      } else if (event.target.closest('[data-loox-close]')) {
        this.dialog.close();
      } else if (event.target === this.dialog) {
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      } else if (event.target.closest('[data-loox-previous], [data-loox-next]')) {
        const direction = event.target.closest('[data-loox-next]') ? 1 : -1;
        this.advanceVideo(direction, true);
      } else if (event.target.closest('[data-loox-review-previous], [data-loox-review-next]')) {
        this.advanceReviews(event.target.closest('[data-loox-review-next]') ? 1 : -1);
      }
    };

    onDialogClose = () => {
      this.stopVideo();
      if (this.lastPlayButton?.isConnected) this.lastPlayButton.focus({ preventScroll: true });
      this.refreshPreview();
    };

    onVideoError = () => {
      if (this.dialog.open && this.player.getAttribute('src')) this.querySelector('[data-loox-video-error]').hidden = false;
    };

    stopVideo() {
      // Removing the iframe releases its document and stops Cloudflare playback/audio.
      this.embed?.remove();
      this.embed = null;
      if (!this.player) return;
      this.player.pause();
      this.player.removeAttribute('src');
      this.player.removeAttribute('poster');
      this.player.load();
    }

    updateArrows = () => {
      const previous = this.querySelector('[data-loox-previous]');
      const next = this.querySelector('[data-loox-next]');
      previous.hidden = next.hidden = this.videoReviews.length <= 1 && !this.nextPage;
      previous.disabled = next.disabled = !!this.feedPromise || this.videoAnimating || this.videoAdvancing;
      const reviewPrevious = this.querySelector('[data-loox-review-previous]');
      const reviewNext = this.querySelector('[data-loox-review-next]');
      if (reviewPrevious && reviewNext) {
        const max = Math.max(0, this.reviews.scrollWidth - this.reviews.clientWidth);
        const position = Math.abs(this.reviews.scrollLeft);
        reviewPrevious.hidden = reviewNext.hidden = max <= 2 && !this.nextPage;
        reviewPrevious.disabled = position <= 2;
        reviewNext.disabled = !!this.feedPromise || (position >= max - 2 && !this.nextPage);
      }
    };
  });
}
